import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, Location } from 'react-router-dom'
import { clearAuthToken, getAuthSessionInfo } from 'modules/auth/api/authService'
import { logoutAzureSession } from 'modules/auth/api/azureAuth'
import { searchRoles } from 'modules/admin/api/roleService'
import { ModuleSelector } from 'shared/components/ModuleSelector'

type NavigationState = {
  user?: any
}

const ADMIN_ROLE_CODE = '10001'

const getState = (location: Location): NavigationState => {
  const state = location.state as NavigationState | null
  if (!state || typeof state !== 'object') {
    return {}
  }
  return state
}

const normalizeToken = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  const token = String(value).trim().toLowerCase()
  return token.length > 0 ? token : null
}

const addRoleCode = (target: Set<string>, value: unknown) => {
  const token = normalizeToken(value)
  if (token) {
    target.add(token)
  }
}

const addRoleCodesFromObject = (target: Set<string>, role: any) => {
  if (!role || typeof role !== 'object') {
    return
  }
  addRoleCode(target, role.code)
  addRoleCode(target, role.roleCode)
  addRoleCode(target, role.role_code)
}

const addRoleId = (target: Set<string>, value: unknown) => {
  const token = normalizeToken(value)
  if (token) {
    target.add(token)
  }
}

const addRoleIdsFromObject = (target: Set<string>, role: any) => {
  if (!role || typeof role !== 'object') {
    return
  }
  addRoleId(target, role.roleId)
  addRoleId(target, role.role_id)
  addRoleId(target, role.id)
}

const hasAdminAccessByRoleCode = (user: any): boolean => {
  if (!user) {
    return false
  }

  const roleCodes = new Set<string>()

  addRoleCode(roleCodes, user.roleCode)
  addRoleCode(roleCodes, user.role_code)
  if (typeof user.role === 'string' && /^\d+$/.test(user.role.trim())) {
    addRoleCode(roleCodes, user.role)
  }

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role: any) => {
      if (typeof role === 'string' && /^\d+$/.test(role.trim())) {
        addRoleCode(roleCodes, role)
      } else {
        addRoleCodesFromObject(roleCodes, role)
      }
    })
  }

  if (Array.isArray(user.tenants)) {
    user.tenants.forEach((tenant: any) => {
      if (Array.isArray(tenant?.roles)) {
        tenant.roles.forEach((role: any) => addRoleCodesFromObject(roleCodes, role))
      }
    })
  }

  return roleCodes.has(ADMIN_ROLE_CODE)
}

const collectRoleIds = (user: any): string[] => {
  if (!user) {
    return []
  }

  const roleIds = new Set<string>()
  addRoleId(roleIds, user.roleId)
  addRoleId(roleIds, user.role_id)

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role: any) => {
      if (typeof role === 'string') {
        addRoleId(roleIds, role)
      } else {
        addRoleIdsFromObject(roleIds, role)
      }
    })
  }

  if (Array.isArray(user.tenants)) {
    user.tenants.forEach((tenant: any) => {
      if (Array.isArray(tenant?.roles)) {
        tenant.roles.forEach((role: any) => addRoleIdsFromObject(roleIds, role))
      }
    })
  }

  return Array.from(roleIds)
}

export function ModuleSelectionPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const { user } = getState(location)
  const directAdminAccess = useMemo(() => hasAdminAccessByRoleCode(user), [user])
  const roleIds = useMemo(() => collectRoleIds(user), [user])
  const [canAccessAdmin, setCanAccessAdmin] = useState<boolean>(directAdminAccess)
  const [isResolvingAdminAccess, setIsResolvingAdminAccess] = useState<boolean>(() => Boolean(user) && !directAdminAccess && roleIds.length > 0)

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
    }
  }, [user, navigate])

  useEffect(() => {
    let cancelled = false

    if (!user) {
      setCanAccessAdmin(false)
      setIsResolvingAdminAccess(false)
      return () => {
        cancelled = true
      }
    }

    if (directAdminAccess) {
      setCanAccessAdmin(true)
      setIsResolvingAdminAccess(false)
      return () => {
        cancelled = true
      }
    }

    if (roleIds.length === 0) {
      setCanAccessAdmin(false)
      setIsResolvingAdminAccess(false)
      return () => {
        cancelled = true
      }
    }

    setIsResolvingAdminAccess(true)

    // Fallback: login payload may contain only roleId (without role code).
    // Resolve active roles once and check whether any assigned roleId maps to code 10001.
    searchRoles({ page: 1, pageSize: 1000, isActive: true })
      .then((response) => {
        if (cancelled) {
          return
        }

        const adminRoleIds = new Set<string>(
          (response.items ?? [])
            .filter((role) => normalizeToken(role.code) === ADMIN_ROLE_CODE)
            .map((role) => normalizeToken(role.roleId))
            .filter((value): value is string => Boolean(value)),
        )

        const matched = roleIds.some((roleId) => {
          const normalized = normalizeToken(roleId)
          return normalized ? adminRoleIds.has(normalized) : false
        })

        setCanAccessAdmin(matched)
        setIsResolvingAdminAccess(false)
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessAdmin(false)
          setIsResolvingAdminAccess(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [directAdminAccess, roleIds, user])

  useEffect(() => {
    if (!user || isResolvingAdminAccess || canAccessAdmin) {
      return
    }

    navigate('/sites/select', { replace: true, state: { user, module: 'E-PTW' } })
  }, [canAccessAdmin, isResolvingAdminAccess, navigate, user])

  if (!user) {
    return null
  }

  if (isResolvingAdminAccess) {
    return null
  }

  if (!canAccessAdmin) {
    return null
  }

  const handleSelectModule = (moduleId: 'E-PTW' | 'ADMIN') => {
    if (moduleId === 'E-PTW') {
      navigate('/sites/select', { replace: true, state: { user, module: moduleId } })
      return
    }
    if (!canAccessAdmin) {
      return
    }

    console.info('Selected admin module for user', user)
    navigate('/admin', { replace: true, state: { user, module: moduleId } })
  }

  const handleBackToLogin = async () => {
    const authSession = getAuthSessionInfo()
    const isContractorAzure =
      authSession?.loginMethod === 'AZURE' &&
      authSession?.userType?.trim().toUpperCase() === 'CONTRACTOR'

    clearAuthToken()

    if (isContractorAzure) {
      try {
        await logoutAzureSession('CONTRACTOR', '/login/contractor')
        return
      } catch (error) {
        console.error('Failed to logout contractor Azure session', error)
      }
    }

    navigate('/login', { replace: true })
  }

  return (
    <ModuleSelector
      currentUser={user}
      showAdminModule={canAccessAdmin}
      onBack={() => {
        void handleBackToLogin()
      }}
      onSelectModule={handleSelectModule}
    />
  )
}
